import React, { useEffect, useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CodeIcon from '@mui/icons-material/Code'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import axiosInstance from '../../../../utils/axiosInstance'
import { fixEmailLogos } from '../../../../utils/emailPreview'

/** Match the server's MIN/MAX/DEFAULT_FOLLOW_UP_DELAY_DAYS (queue/cvFollowUpMail.ts). */
export const MIN_FOLLOW_UP_DELAY_DAYS = 1
export const MAX_FOLLOW_UP_DELAY_DAYS = 30
export const DEFAULT_FOLLOW_UP_DELAY_DAYS = 3

export interface CvFollowUpValues {
  autoFollowUpEnabled?: boolean
  followUpDelayDays?: number
  followUpCustomTitle?: string
  followUpCustomText?: string
}

interface CvFollowUpSettingsProps {
  customerId: string
  values: CvFollowUpValues
  onChange: (values: CvFollowUpValues) => void
  isEditMode: boolean
  /**
   * The CV request is the customer's own email without the upload button. The server does
   * not follow those up, since the applicant may have sent the CV another way.
   */
  cvRequestWithoutUploadButton: boolean
  /** The CV request goes out automatically for new leads. */
  autoAskForCv: boolean
}

const accordionSx = {
  mb: 2,
  bgcolor: 'grey.100',
  '&.MuiAccordion-root': {
    border: '1px solid',
    borderColor: 'grey.300',
  },
  '& .MuiAccordionSummary-root': {
    bgcolor: 'grey.200',
    '&:hover': {
      bgcolor: 'grey.300',
    },
  },
}

/**
 * The CV follow-up part of the email settings: a reminder to applicants who were asked
 * for their CV and have not uploaded one after some days. Sent by the server's
 * queue/cvFollowUpMail.ts; the preview is built by the server with the same code.
 */
const CvFollowUpSettings = ({
  customerId,
  values,
  onChange,
  isEditMode,
  cvRequestWithoutUploadButton,
  autoAskForCv,
}: CvFollowUpSettingsProps) => {
  const [expanded, setExpanded] = useState(false)
  const delayDays = values.followUpDelayDays ?? DEFAULT_FOLLOW_UP_DELAY_DAYS
  // The field's own text, so it can be empty while the user types; `values` holds the number.
  const [daysText, setDaysText] = useState(String(delayDays))
  useEffect(() => {
    setDaysText((text) => (Number(text) === delayDays ? text : String(delayDays)))
  }, [delayDays])

  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null)
  const [previewError, setPreviewError] = useState('')
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)

  const enabled = !!values.autoFollowUpEnabled
  // Switching it off stays possible, so a customer who changes their CV request later can
  // still turn the follow-up off.
  const canEnable = !cvRequestWithoutUploadButton

  const openPreview = async () => {
    setIsLoadingPreview(true)
    setPreviewError('')
    try {
      const response = await axiosInstance.post(
        `/customer-email-settings/${customerId}/follow-up-preview`,
        {
          followUpCustomTitle: values.followUpCustomTitle || '',
          followUpCustomText: values.followUpCustomText || '',
        }
      )
      const data = response?.data?.data
      setPreview({ subject: data?.subject || '', html: fixEmailLogos(data?.html || '') })
    } catch (error) {
      setPreviewError(String(error))
    } finally {
      setIsLoadingPreview(false)
    }
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={accordionSx}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">CV follow-up</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>How it works</AlertTitle>
          Applicants who were asked for their CV and have not uploaded one after the chosen number
          of days get one reminder with the upload button. It is sent between 08:00 and 20:00, and
          only for CV requests sent after the follow-up was switched on.
        </Alert>

        {cvRequestWithoutUploadButton && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The CV request uses your own email without the upload button (CV request type
            &quot;custom&quot;). Applicants may send their CV another way, so the follow-up cannot
            tell whether it is missing and is not available.
          </Alert>
        )}
        {!cvRequestWithoutUploadButton && !autoAskForCv && enabled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The automatic CV request is off, so only applicants asked for their CV by hand get a
            follow-up.
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={enabled}
              onChange={(e) => onChange({ ...values, autoFollowUpEnabled: e.target.checked })}
              disabled={!isEditMode || (!enabled && !canEnable)}
            />
          }
          label="Send a CV follow-up"
          sx={{ mb: 2 }}
        />

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
            Send after
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p">
            Days after the CV request, {MIN_FOLLOW_UP_DELAY_DAYS} to {MAX_FOLLOW_UP_DELAY_DAYS}. If
            this customer&apos;s ATS waits for the CV before sending a lead, a CV that arrives after
            that wait is not sent to the ATS (it still reaches the customer by email).
          </Typography>
          <TextField
            size="small"
            variant="outlined"
            value={daysText}
            disabled={!isEditMode}
            onChange={(e) => {
              // Digits only, so the field can be emptied while typing a new number.
              const digits = e.target.value.replace(/\D/g, '').slice(0, 2)
              setDaysText(digits)
              if (digits === '') return
              const days = Math.min(
                Math.max(Number(digits), MIN_FOLLOW_UP_DELAY_DAYS),
                MAX_FOLLOW_UP_DELAY_DAYS
              )
              if (String(days) !== digits) setDaysText(String(days))
              onChange({ ...values, followUpDelayDays: days })
            }}
            onBlur={() => {
              if (daysText === '') setDaysText(String(delayDays))
            }}
            inputProps={{ inputMode: 'numeric', 'aria-label': 'Send after, in days' }}
            InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
            sx={{
              mt: 1,
              width: 160,
              '& .MuiOutlinedInput-root': { borderRadius: 1, bgcolor: 'background.paper' },
            }}
          />
        </Box>

        <TextField
          fullWidth
          value={values.followUpCustomTitle || ''}
          onChange={(e) => onChange({ ...values, followUpCustomTitle: e.target.value })}
          placeholder="Deine Bewerbung als [Stellenbezeichnung] bei [Kunde]"
          disabled={!isEditMode}
          label="Custom Email Subject"
          helperText="Leave empty for the default subject. [Kunde] and [Stellenbezeichnung] are replaced with the company name and job title."
          sx={{ mb: 2 }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1.5,
            p: 1.5,
            border: '1px dashed',
            borderColor: 'primary.light',
            borderRadius: 1,
            bgcolor: 'primary.lighter',
          }}
        >
          <CodeIcon fontSize="small" color="primary" />
          <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>
            Available variables:
          </Typography>
          <Chip size="small" label="{{name}}" color="primary" variant="outlined" />
          <Chip size="small" label="{{jobTitle}}" color="primary" variant="outlined" />
          <Chip size="small" label="{{companyName}}" color="primary" variant="outlined" />
          <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
            Paste these into your text — they will be replaced with the candidate&apos;s name, job
            title, and company name.
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={4}
          value={values.followUpCustomText || ''}
          onChange={(e) => onChange({ ...values, followUpCustomText: e.target.value })}
          placeholder={
            'Vor ein paar Tagen haben wir Dich gebeten, Deinen Lebenslauf hochzuladen. ' +
            'Bisher haben wir noch keinen Lebenslauf von Dir erhalten. …'
          }
          disabled={!isEditMode}
          label="Custom Email Text"
          helperText="Leave empty for the default text. The greeting, the upload button and the signature are always added."
        />

        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={openPreview}
            disabled={isLoadingPreview}
          >
            Preview CV Follow-up Email
          </Button>
          {previewError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {previewError}
            </Alert>
          )}
        </Box>

        <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Typography variant="h6">CV Follow-up Email Preview</Typography>
            <Typography variant="body2" color="text.secondary">
              Subject: {preview?.subject}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
              <iframe
                srcDoc={preview?.html || ''}
                title="CV Follow-up Email Preview"
                style={{ width: '100%', height: '600px', border: 'none' }}
                sandbox=""
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreview(null)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  )
}

export default CvFollowUpSettings
